import type {
  DataFetchParams,
  DataFetchResponse,
  FilterOptionsParams,
  DynamicFilterOptionsPage,
  ResponseMeta,
} from '@/components/DataTable';
import {
  listOrganizationMCPServers,
  getMCPFilterOptions,
} from '@kirin/api-client/api/mcp';
import type {
  MCPEnforcementSummary,
  MCPEnforcementView,
  MCPFilterOptionField,
  MCPGatewaySyncState,
  MCPServerListItem,
  MCPSortField,
} from '@kirin/api-client/contracts/mcp';
import { apiClient } from '@/lib/api-client';

/**
 * MCP table meta: the shared shape plus gateway sync state (KIR-3940) and the
 * New / Blocked counts (KIR-4482).
 */
export type MCPTableMeta = ResponseMeta & {
  gatewaySync?: MCPGatewaySyncState;
  enforcementSummary?: MCPEnforcementSummary;
};

/**
 * KIR-4482 enforcement options for {@link fetchMCPServers}. Omitted while
 * `mcp_inventory_enforcement` is off; `view` is the New / Blocked chip
 * (`null` = All servers).
 */
export interface MCPEnforcementFetchOptions {
  view: MCPEnforcementView | null;
  /** KIR-4701: resolve each row's Allow/Block for this security group. */
  securityGroupId?: string | null;
}

/**
 * Fetch MCP servers for the DataTable component.
 *
 * One read path, whatever the gateway-control flag says (KIR-5042). The flag
 * previously swapped this for an in-memory fixture store, which meant the
 * union endpoint, its `provenance` branch selector, its sorting and its
 * filtering — everything KIR-3940 slices 2 and 3 built — were unreachable from
 * the UI. The endpoint has handled `provenance` as a branch selector since
 * slice 2b, so the flag no longer needs to change how data is fetched at all;
 * it only decides which COLUMNS the table shows.
 */
export async function fetchMCPServers(
  organizationId: string,
  params: DataFetchParams,
  signal?: AbortSignal,
  enforcement?: MCPEnforcementFetchOptions
): Promise<DataFetchResponse<MCPServerListItem, MCPTableMeta>> {
  const sortField = params.sort?.field as MCPSortField | undefined;
  const response = await listOrganizationMCPServers(
    apiClient,
    organizationId,
    {
      page: params.pagination.pageIndex + 1,
      limit: params.pagination.pageSize,
      filters: params.filters,
      sortBy: sortField,
      sortOrder: params.sort?.order,
      search: params.search,
      knownTotal: params.knownTotal,
      enforcementView: enforcement?.view ?? undefined,
      securityGroupId: enforcement?.securityGroupId ?? undefined,
    },
    { signal }
  );

  return {
    data: response.data,
    pagination: response.pagination,
    meta: {
      activeFilters: response.meta?.activeFilters ?? [],
      search: response.meta?.search,
      // Read off the response, not off a fixture store (KIR-5042). The union
      // endpoint has returned this since KIR-3940 slice 2b; the table was
      // reading an in-memory stand-in and never saw it.
      gatewaySync: response.meta?.gatewaySync,
      enforcementSummary: response.meta?.enforcementSummary,
    },
  };
}

/**
 * Fetch dynamic filter options for name, version, publisher, or users fields.
 * Returns either string[] or rich option objects depending on the field.
 */
export async function fetchMCPFieldFilterOptions(
  organizationId: string,
  field: MCPFilterOptionField,
  params: FilterOptionsParams
): Promise<DynamicFilterOptionsPage> {
  const response = await getMCPFilterOptions(apiClient, organizationId, {
    field,
    search: params.search,
    limit: params.limit,
    offset: params.offset,
  });

  return {
    options: response.options,
    total: response.pagination.total,
    nextOffset: response.pagination.nextOffset ?? null,
    hasMore: response.pagination.hasMore,
  };
}
